<template>
    <div class="order-block">
        <div class="order-header d-flex justify-space-between align-center mb-4">
            <div class="shop-name font-weight-bold">
                {{ order.seller?.shopName || 'ไม่ระบุร้านค้า' }}
            </div>
            <div class="order-status" :style="{ color: getStatusColor(order.currentStatus) }">
                {{ getStatusLabel(order.currentStatus) }}
            </div>
        </div>

        <!-- Cancel reason banner -->
        <div v-if="isCancelled && order.cancelReason" class="cancel-reason-box mb-3 d-flex align-start">
            <v-icon small color="error" class="mr-2 mt-1">mdi-alert-circle-outline</v-icon>
            <div>
                <span class="cancel-reason-label">เหตุผลการยกเลิก: </span>
                <span class="cancel-reason-text">{{ order.cancelReason }}</span>
            </div>
        </div>

        <div v-for="item in order.items" :key="item.id" class="product-item d-flex mb-4">
            <v-img :src="getProductImage(item.product)" aspect-ratio="1" cover class="rounded-xl border-img shrink-0"
                :max-width="$vuetify.breakpoint.smAndDown ? 72 : 120"
                :max-height="$vuetify.breakpoint.smAndDown ? 72 : 120" />
            <div class="product-details grow pl-3">
                <div class="product-name font-weight-bold mb-1">{{ item.productName }}</div>
                <div class="product-price grey--text text--darken-2">
                    {{ formatPrice(item.price) }} บาท
                </div>
                <div class="product-qty mt-1">x{{ item.quantity }}</div>
            </div>
        </div>

        <div class="order-footer d-flex flex-column align-end">
            <!-- Tracking style -->
            <template v-if="isTracking">
                <div class="order-summary mb-3">
                    รวมการสั่งซื้อ: <span class="font-weight-bold">{{ formatPrice(order.grandTotal || order.totalPrice) }} บาท</span>
                </div>
                <div class="action-btns d-flex" style="gap: 8px;">
                    <v-btn v-if="['pending', 'request_paid'].includes(order.currentStatus)" color="primary"
                        class="btn-action rounded-lg px-4 text-none white--text" height="36" @click="goToPayment">
                        ชำระเงิน
                    </v-btn>

                    <v-btn v-if="['shipped', 'out_for_delivery'].includes(order.currentStatus)" color="success"
                        class="btn-action rounded-lg px-4 text-none white--text" height="36" @click="confirmReceive"
                        :loading="receiving">
                        ยืนยันรับสินค้า
                    </v-btn>

                    <v-btn
                        v-if="canReview"
                        color="#F4811F" dark class="btn-action rounded-lg px-4 text-none" height="36" elevation="0"
                        @click="showReviewDialog = true"
                    >
                        <v-icon left small>mdi-star-outline</v-icon>รีวิว
                    </v-btn>

                    <v-btn outlined color="grey darken-1" class="btn-action rounded-lg px-4 text-none" height="36"
                        @click="openSellerChat">
                        <v-icon left small>mdi-chat-processing-outline</v-icon>
                        ติดต่อผู้ขาย
                    </v-btn>
                    <v-btn outlined color="grey darken-1" class="btn-action rounded-lg px-4 text-none" height="36"
                        :to="{ name: 'order-detail', params: { id: order.id } }">
                        ดูรายละเอียด
                    </v-btn>
                </div>
            </template>
            <!-- History style -->
            <template v-else>
                <div class="order-summary mb-3">
                    สินค้า {{ order.items?.length || 0 }} รายการ: <span class="font-weight-bold">{{
                        formatPrice(order.grandTotal || order.totalPrice) }} บาท</span>
                </div>
                <div class="action-btns d-flex" style="gap:8px">
                    <v-btn
                        v-if="canReview"
                        color="#F4811F" dark class="btn-action rounded-lg px-4 text-none" height="36" elevation="0"
                        @click="showReviewDialog = true"
                    >
                        <v-icon left small>mdi-star-outline</v-icon>รีวิว
                    </v-btn>
                    <v-chip v-else-if="isDelivered" x-small label color="#f0fdf4" text-color="#16a34a" class="align-self-center">
                        <v-icon left size="12">mdi-check-circle-outline</v-icon>รีวิวแล้ว
                    </v-chip>
                    <v-btn outlined color="grey darken-1" class="btn-action rounded-lg px-4 text-none" height="36"
                        :to="{ name: 'order-detail', params: { id: order.id } }">
                        ดูรายละเอียด
                    </v-btn>
                </div>
            </template>
        </div>
        <ReviewDialog
            v-model="showReviewDialog"
            :order="order"
            @reviewed="$emit('refresh')"
        />
    </div>
</template>

<script>
import { getProductImage } from "@/utils/image";
import { receiveOrderApi } from "@/services/api/order";
import Swal from "sweetalert2";
import ReviewDialog from "./ReviewDialog.vue";

export default {
    name: "OrderCard",
    components: { ReviewDialog },
    data() {
        return {
            receiving: false,
            showReviewDialog: false,
        }
    },
    props: {
        order: {
            type: Object,
            required: true
        },
        isTracking: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        isDelivered() {
            return this.order?.currentStatus === "delivered" || this.order?.status === "delivered";
        },
        isCancelled() {
            return this.order?.currentStatus === "cancelled" || this.order?.status === "cancelled";
        },
        canReview() {
            if (!this.isDelivered) return false;
            const allSellerDone = !!this.order?.sellerReview;
            const allRiderDone = !this.order?.riderId || !!this.order?.riderReview;
            const allProductsDone = (this.order?.items || []).every(i => !!i.productReview);
            return !(allSellerDone && allRiderDone && allProductsDone);
        },
    },

    methods: {
        getProductImage,
        formatPrice(price) {
            return parseFloat(price || 0).toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
        },
        getStatusLabel(status) {
            const dict = {
                pending: "รอชำระเงิน",
                request_paid: "รอยืนยันการชำระเงิน",
                paid: "ชำระเงินแล้ว",
                processing: "กำลังเตรียมสินค้า/ดำเนินการ",
                seeking_rider: "กำลังเรียกไรเดอร์",
                rider_accepted: "ไรเดอร์รับงานแล้ว",
                picked_up: "ไรเดอร์รับสินค้าแล้ว",
                packed: "กำลังแพ็คสินค้า",
                shipped: "บริษัทขนส่งเข้ารับพัสดุแล้ว",
                out_for_delivery: "กำลังนำจัดส่ง",
                delivering: "กำลังนำส่ง",
                delivered: "การจัดส่งสำเร็จ",
                cancelled: "ยกเลิกแล้ว",
                refunded: "คืนเงินแล้ว",
            };
            return dict[status] || status;
        },
        getStatusColor(status) {
            const colors = {
                pending: "#6366f1",
                request_paid: "#f59e0b",
                paid: "#16a34a",
                processing: "#3b82f6",
                seeking_rider: "#f59e0b",
                rider_accepted: "#3b82f6",
                picked_up: "#10b981",
                packed: "#3b82f6",
                shipped: "#F4811F",
                out_for_delivery: "#8b5cf6",
                delivering: "#8b5cf6",
                delivered: "#16a34a",
                cancelled: "#ef4444",
                refunded: "#6b7280",
            };
            
            if (this.isTracking) {
                // Tracking style might want to emphasize certain colors
                return colors[status] || "#d6230b";
            }
            return colors[status] || "#198C19";
        },
        goToPayment() {
            this.$router.push({
                name: 'qr-payment',
                params: {
                    orderId: this.order.id,
                    orderIds: [this.order.id],
                    total: this.order.grandTotal
                }
            });
        },
        async confirmReceive() {
            if (this.receiving) return;

            const result = await Swal.fire({
                title: 'ยืนยันการรับสินค้า?',
                text: "คุณได้ตรวจสอบและยอมรับสินค้านี้แล้วใช่หรือไม่?",
                icon: 'question',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'ยืนยัน',
                cancelButtonText: 'ยกเลิก',
                reverseButtons: true
            });

            if (!result.isConfirmed) return;

            this.receiving = true;
            try {
                await receiveOrderApi(this.order.id);
                Swal.fire({
                    title: 'สำเร็จ',
                    text: 'ยืนยันการรับสินค้าเรียบร้อยแล้ว',
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false
                });
                this.$emit('refresh');
            } catch (error) {
                console.error("Error receiving order:", error);
                Swal.fire({
                    title: 'เกิดข้อผิดพลาด',
                    text: error.response?.data?.message || 'ไม่สามารถยืนยันรับสินค้าได้',
                    icon: 'error'
                });
            } finally {
                this.receiving = false;
            }
        },
        openSellerChat() {
            const sellerId = this.order?.sellerId;
            if (!sellerId) return;
            this.$root.$emit("open-floating-chat", sellerId);
        },
    }
}
</script>

<style scoped>
.order-block {
    padding: 0;
}

.shop-name {
    font-size: 16px;
    color: #000;
}

.order-status {
    font-size: 14px;
    font-weight: 500;
}

.product-item {
    align-items: flex-start;
}

.product-name {
    font-size: 15px;
    color: #000;
    line-height: 1.4;
}

.product-price {
    font-size: 14px;
}

.product-qty {
    font-size: 14px;
    color: #757575;
}

.order-summary {
    font-size: 14px;
    color: #000;
}

.cancel-reason-box {
    background: #fff5f5;
    border: 1px solid #fecaca;
    border-radius: 8px;
    padding: 10px 12px;
    font-size: 13px;
    color: #b91c1c;
}

.cancel-reason-label {
    font-weight: 600;
}

.cancel-reason-text {
    font-weight: 400;
}

.btn-action {
    font-weight: 500;
    font-size: 13px !important;
}

@media (max-width: 600px) {
    .product-item {
        flex-direction: row;
        align-items: flex-start;
    }

    .order-header {
        flex-direction: column;
        align-items: flex-start !important;
        gap: 4px;
    }

    .order-footer {
        align-items: flex-start !important;
        width: 100%;
    }

    .action-btns {
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
        width: 100%;
    }

    .action-btns .btn-action {
        flex-shrink: 0;
    }

    .order-summary {
        width: 100%;
        text-align: left;
    }
}
</style>
